import { KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useSearchParams } from "react-router-dom";
import { ApiError } from "@/shared/api";
import { FORBIDDEN_MODULE_NOT_ENABLED_PATH } from "@/shared/lib/forbiddenReason";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, BellOff, Headphones } from "lucide-react";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { useMutationWithToast } from "@/shared/hooks/useMutationWithToast";
import { aiKefuService, type CsSession, type PageResponse } from "../api/aiKefuService";
import { SummaryCards } from "../ui/ai-kefu/SummaryCards";
import { SessionsTab } from "../ui/ai-kefu/SessionsTab";
import { ScenePreferenceCard } from "../ui/ai-kefu/ScenePreferenceCard";
import { OptimizeTab } from "../ui/ai-kefu/OptimizeTab";
import { TakeoverDialog } from "../ui/ai-kefu/TakeoverDialog";
import { mergeSearchParamsWithTab } from "../ui/ai-kefu/helpers";
import { useAuth } from "@/contexts/AuthContext";
import { isTenantManagerPlus } from "@/shared/lib/rbac";
import { useOrgScopeFilter } from "@/shared/hooks/useOrgScopeFilter";
import { kefuHumanOnly } from "../model/kefuHumanOnly";
import { KefuHumanOnlyBanner } from "./KefuHumanOnlyBanner";
import { useKefuPending } from "@/entities/cs-pending";
import { Button } from "@/shared/ui/button";
import { KefuNoChannelBanner } from "../ui/ai-kefu/KefuNoChannelBanner";
import {
  SESSIONS_POLL_MS,
  messagesPollMs,
  playPendingChime,
  readPendingSoundPref,
  shouldChime,
  takeoverModeOf,
  titleWithPending,
  writePendingSoundPref,
} from "../model/kefuRealtime";

export default function AiKefu() {
  const { t } = useTranslation("customer-service");
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, permissions } = useAuth();
  // 1002:客服舱停用(暂停 / 过期,历史上拥有过)→ 只读 + 人工处理:只留会话页,AI 相关入口(统计、偏好、优化、接管)全隐藏
  const humanOnly = kefuHumanOnly(permissions);
  const rawTab = searchParams.get("tab") || "sessions";
  // 旧 URL ?tab=test 回落到默认会话管理，避免白屏
  const tab = humanOnly || rawTab === "overview" || rawTab === "test" ? "sessions" : rawTab;
  const queryClient = useQueryClient();

  // 1010 一期补完 B5:`/ai-kefu?session=<id>` 深链(获客卡 / 铃铛 / 企微提醒卡都链到这里)—— 首帧就选中,不闪空。
  const deepLinkSession = searchParams.get("session");
  const [selectedSessionId, setSelectedSessionId] = useState<string>(() => deepLinkSession ?? "");
  useEffect(() => {
    if (!deepLinkSession) return;
    setSelectedSessionId(deepLinkSession);
    // 吃掉参数(replace,不进历史):否则关掉详情后刷新又被选回来;同时落到会话页签。
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("session");
        next.set("tab", "sessions");
        return next;
      },
      { replace: true }
    );
  }, [deepLinkSession, setSearchParams]);
  const [replyInput, setReplyInput] = useState("");
  const [markAsBenchmark, setMarkAsBenchmark] = useState(false);
  const [takeoverTarget, setTakeoverTarget] = useState<string | null>(null);

  const {
    data: csStats,
    isError: csStatsError,
    refetch: refetchCsStats,
  } = useQuery({
    queryKey: ["cs-stats"],
    queryFn: aiKefuService.getStats,
    enabled: !humanOnly,
  });

  const sessionSearch = searchParams.get("q") ?? "";
  const sessionStatus = searchParams.get("status") ?? "all";
  const sessionChannel = searchParams.get("channel") ?? "all";
  const sessionPage = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);
  const sessionSize = Math.min(100, Math.max(1, Number(searchParams.get("size") ?? "20") || 20));

  // V22 (2026-05-15) — "只看我的接管" toggle (MANAGER+ 可见; STAFF 后端强制隔离不显示)
  const canSwitchScope = isTenantManagerPlus(user?.role);
  // #9 (2026-07-05) — 组织级归属筛选(共享单元,替代旧「只看我的接管」toggle);MANAGER+ 按接管坐席归属切。
  // scope=all 保 PD-CS-1 全租户共享读(后端不塌回 own)。切归属维度自动回第 1 页(resetKeys=["page"])。
  const orgFilter = useOrgScopeFilter({ canSwitchScope });
  const { scopeParams } = orgFilter;

  const sessionsQueryParams = {
    page: sessionPage,
    size: sessionSize,
    search: sessionSearch,
    status: sessionStatus,
    channel: sessionChannel,
    ...scopeParams,
  };

  const {
    data: sessionsPage,
    isLoading: sessionsLoading,
    isError: sessionsError,
    error: sessionsErr,
    refetch: refetchSessions,
  } = useQuery<PageResponse<CsSession>>({
    queryKey: ["cs-sessions", sessionsQueryParams],
    queryFn: () => aiKefuService.listSessions(sessionsQueryParams),
    enabled: tab === "sessions",
    placeholderData: (prev) => prev,
    // B3:页面可见时 15s 刷新(react-query 默认页面隐藏不跑 refetchInterval)
    refetchInterval: SESSIONS_POLL_MS,
  });
  const sessions = sessionsPage?.records ?? [];
  const sessionsTotal = sessionsPage?.total ?? 0;

  const updateSessionsParam = (
    patch: Partial<{ q: string; status: string; channel: string; page: number; size: number }>
  ) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", "sessions");
    if ("q" in patch) {
      if (patch.q) next.set("q", patch.q);
      else next.delete("q");
    }
    if ("status" in patch) {
      if (patch.status && patch.status !== "all") next.set("status", patch.status);
      else next.delete("status");
    }
    if ("channel" in patch) {
      if (patch.channel && patch.channel !== "all") next.set("channel", patch.channel);
      else next.delete("channel");
    }
    if ("size" in patch && patch.size) next.set("size", String(patch.size));
    // 任何筛选变化都回到第 1 页，除非调用方显式传了 page
    next.set("page", String(patch.page ?? 1));
    setSearchParams(next);
  };

  const {
    data: sessionDetail,
    isError: sessionDetailError,
    refetch: refetchSessionDetail,
  } = useQuery({
    queryKey: ["cs-session-detail", selectedSessionId],
    queryFn: () => aiKefuService.getSessionDetail(selectedSessionId),
    enabled: tab === "sessions" && !!selectedSessionId,
    // 状态会在服务端变(AI 转人工 / 被别人接手 / 自动结束)—— 与消息同频刷新
    refetchInterval: (q) => messagesPollMs(q.state.data),
  });
  // B3:打开的是网页 / H5 会话 → 人工处理中 4s、AI 接待中 10s 刷消息;企微 / 已结束不轮询
  const messagesRefetchMs = messagesPollMs(sessionDetail);

  const MESSAGES_PAGE_SIZE = 50;
  const {
    data: messagesPages,
    fetchNextPage: fetchEarlierMessages,
    hasNextPage: hasEarlierMessages,
    isFetchingNextPage: isLoadingEarlierMessages,
    isError: messagesError,
    refetch: refetchMessages,
  } = useInfiniteQuery({
    queryKey: ["cs-session-messages", selectedSessionId],
    enabled: tab === "sessions" && !!selectedSessionId,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      aiKefuService.getSessionMessages(selectedSessionId, pageParam, MESSAGES_PAGE_SIZE),
    refetchInterval: messagesRefetchMs,
    getNextPageParam: (lastPage) => {
      // lastPage 是"最新 → 更旧"方向上的最后一页；next 继续往更旧翻
      const loaded = lastPage.page * lastPage.size;
      return loaded < lastPage.total ? lastPage.page + 1 : undefined;
    },
  });
  // 后端每页降序（最新在前，见 CsQueryService.getSessionMessages「降序:最新在前」）。
  // 把所有页 flatten 后整体 reverse → 全局时间升序（旧在上、新在下），对话正序展示
  // （客户先问在上、AI 后答在下）。此前误以为页内是旧→新、只 reverse 页顺序，单页会话
  // 直接拿到降序 → AI 回复排到客户问题上面（顺序反）。
  const sessionMessages = useMemo(() => {
    if (!messagesPages) return [];
    return messagesPages.pages.flatMap((p) => p.records).reverse();
  }, [messagesPages]);

  // 未命中看板:全量拉取(所有状态)。优化 Tab 的 hot TOP5「含已处理」+ 建议要跨全量算,open 清单分页在 OptimizeTab
  // 客户端做(对齐 F 评估/场景偏好客户端分页;每问题 1 行、列表轻)。
  const { data: unmatchedQuestions = [] } = useQuery({
    queryKey: ["cs-unmatched-questions"],
    queryFn: () => aiKefuService.listUnmatchedQuestions(),
    enabled: tab === "optimize",
  });

  const unmatchedStatusMutation = useMutationWithToast({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      aiKefuService.updateUnmatchedQuestionStatus(id, status),
    successToast: t("toast.unmatchedStatusUpdated"),
    errorToast: t("toast.updateFailed"),
    invalidateKeys: [["cs-unmatched-questions"]],
  });

  const takeoverMutation = useMutationWithToast<
    void,
    { sessionId: string; servicerUserid?: string }
  >({
    mutationFn: ({ sessionId, servicerUserid }) =>
      aiKefuService.takeoverSession(sessionId, servicerUserid),
    // 企微:接管 = 派给企微接待人员 →「已通知 xxx」;网页 / H5:接管人 = 我 → 在会话详情底部直接回复
    successToast: (_, vars) => ({
      title: t("toast.takeoverSuccessTitle"),
      description: vars.servicerUserid
        ? t("toast.takeoverSuccessDesc", { userid: vars.servicerUserid })
        : t("toast.takeoverSuccessDirectDesc"),
    }),
    errorToast: t("toast.takeoverFailed"),
    invalidateKeys: [["cs-sessions"], ["cs-pending"]],
    onSuccess: (_, vars) => {
      setTakeoverTarget(null);
      if (!vars.servicerUserid) {
        // 直接接管后打开该会话,回复框就在眼前
        setSelectedSessionId(vars.sessionId);
        queryClient.invalidateQueries({ queryKey: ["cs-session-detail", vars.sessionId] });
        queryClient.invalidateQueries({ queryKey: ["cs-session-messages", vars.sessionId] });
      }
    },
  });

  const handleTakeover = (sessionId: string, channel?: string | null) => {
    if (takeoverModeOf(channel) === "wecom_dialog") setTakeoverTarget(sessionId);
    else takeoverMutation.mutate({ sessionId });
  };

  // B4:待处理提醒 —— 标题前缀「(N) 」+ 可选提示音(默认关)
  const pending = useKefuPending();
  const waitingHuman = pending?.waitingHuman;
  const [soundOn, setSoundOn] = useState<boolean>(() => readPendingSoundPref());
  const prevWaitingRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (soundOn && shouldChime(prevWaitingRef.current, waitingHuman)) playPendingChime();
    prevWaitingRef.current = waitingHuman;
  }, [waitingHuman, soundOn]);
  useEffect(() => {
    document.title = titleWithPending(document.title, waitingHuman ?? 0);
    return () => {
      document.title = titleWithPending(document.title, 0);
    };
  }, [waitingHuman]);
  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    writePendingSoundPref(next);
    if (next) playPendingChime();
  };

  const closeMutation = useMutationWithToast({
    mutationFn: (sessionId: string) => aiKefuService.closeSession(sessionId),
    successToast: t("toast.sessionClosed"),
    errorToast: t("toast.closeFailed"),
    invalidateKeys: [["cs-sessions"]],
  });

  const replyMutation = useMutationWithToast<
    unknown,
    { sessionId: string; content: string; markAsBenchmark: boolean }
  >({
    mutationFn: ({ sessionId, content, markAsBenchmark: mark }) =>
      aiKefuService.sendHumanReply(sessionId, content, mark),
    successToast: (_, vars) =>
      vars.markAsBenchmark
        ? { title: t("toast.replySuccessTitle"), description: t("toast.replySuccessDesc") }
        : undefined,
    errorToast: t("toast.replyFailed"),
    invalidateKeys: [
      ["cs-session-messages", selectedSessionId],
      ["cs-session-detail", selectedSessionId],
      ["cs-sessions"],
      ["cs-pending"],
    ],
    onSuccess: () => {
      setReplyInput("");
      setMarkAsBenchmark(false);
    },
  });

  const handleSendReply = () => {
    const content = replyInput.trim();
    if (!content || !selectedSessionId) return;
    replyMutation.mutate({ sessionId: selectedSessionId, content, markAsBenchmark });
  };

  const handleReplyKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  const handleRefreshSessions = () => {
    queryClient.invalidateQueries({ queryKey: ["cs-sessions"] });
    if (selectedSessionId) {
      queryClient.invalidateQueries({ queryKey: ["cs-session-detail", selectedSessionId] });
      queryClient.invalidateQueries({ queryKey: ["cs-session-messages", selectedSessionId] });
    }
  };

  const handleRefreshDetail = () => {
    if (!selectedSessionId) return;
    queryClient.invalidateQueries({ queryKey: ["cs-session-detail", selectedSessionId] });
    queryClient.invalidateQueries({ queryKey: ["cs-session-messages", selectedSessionId] });
  };

  // 1002:停用模式下后端仍拒(历史上没拥有过客服舱 → NOT_OWNED)= 真没开通,转「未开通」页,不留一屏报错。
  if (
    humanOnly &&
    sessionsErr instanceof ApiError &&
    sessionsErr.errorCode === "MODULE_NOT_ENABLED"
  ) {
    return <Navigate to={FORBIDDEN_MODULE_NOT_ENABLED_PATH} replace />;
  }

  return (
    <>
      <div className="space-y-5">
        <div className="flex items-center gap-3 fx-enter">
          <div className="p-2.5 rounded-lg bg-primary/10">
            <Headphones className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold text-foreground">{t("page.title")}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{t("page.subtitle")}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 text-xs text-muted-foreground"
            onClick={toggleSound}
            aria-pressed={soundOn}
            data-testid="kefu-sound-toggle"
            title={t("pending.soundHint")}
          >
            {soundOn ? <Bell className="h-4 w-4 mr-1" /> : <BellOff className="h-4 w-4 mr-1" />}
            {soundOn ? t("pending.soundOn") : t("pending.soundOff")}
          </Button>
        </div>

        {/* 1010 一期补完 B1:企微客服与网页 / H5 是并列渠道 —— 两边都没接才提示,不再写「需先绑定企业微信」 */}
        <KefuNoChannelBanner enabled={!humanOnly} />

        {humanOnly ? (
          <KefuHumanOnlyBanner />
        ) : (
          <>
            <QueryErrorBanner
              isError={csStatsError}
              message={t("queryError.stats")}
              onRetry={() => refetchCsStats()}
            />
            <SummaryCards stats={csStats} />
          </>
        )}

        <Tabs
          value={tab}
          onValueChange={(v) => setSearchParams(mergeSearchParamsWithTab(searchParams, v))}
        >
          <TabsList>
            <TabsTrigger value="sessions">{t("tabs.sessions")}</TabsTrigger>
            {!humanOnly && (
              <>
                <TabsTrigger value="preferences">{t("tabs.preferences")}</TabsTrigger>
                <TabsTrigger value="optimize">{t("tabs.optimize")}</TabsTrigger>
              </>
            )}
          </TabsList>

          <TabsContent value="sessions">
            <QueryErrorBanner
              isError={sessionsError}
              message={t("queryError.sessions")}
              onRetry={() => refetchSessions()}
              className="mt-4"
            />
            <QueryErrorBanner
              isError={sessionDetailError}
              message={t("queryError.sessionDetail")}
              onRetry={() => refetchSessionDetail()}
              className="mt-4"
            />
            <QueryErrorBanner
              isError={messagesError}
              message={t("queryError.messages")}
              onRetry={() => refetchMessages()}
              className="mt-4"
            />
            <SessionsTab
              sessions={sessions}
              total={sessionsTotal}
              page={sessionPage}
              pageSize={sessionSize}
              search={sessionSearch}
              status={sessionStatus}
              onSearchSubmit={(q) => updateSessionsParam({ q })}
              onStatusChange={(status) => updateSessionsParam({ status })}
              channel={sessionChannel}
              onChannelChange={(channel) => updateSessionsParam({ channel })}
              onPageChange={(page) => updateSessionsParam({ page })}
              isLoading={sessionsLoading}
              onRefresh={handleRefreshSessions}
              orgFilter={orgFilter}
              selectedSessionId={selectedSessionId}
              onSelectSession={setSelectedSessionId}
              onCloseDetail={() => setSelectedSessionId("")}
              sessionDetail={sessionDetail}
              sessionMessages={sessionMessages}
              onLoadEarlierMessages={() => fetchEarlierMessages()}
              hasEarlierMessages={hasEarlierMessages}
              isLoadingEarlierMessages={isLoadingEarlierMessages}
              onRefreshDetail={handleRefreshDetail}
              takeoverPendingId={
                takeoverMutation.isPending ? takeoverMutation.variables?.sessionId : undefined
              }
              closePendingId={
                closeMutation.isPending ? (closeMutation.variables as string) : undefined
              }
              onTakeover={humanOnly ? undefined : handleTakeover}
              onCloseSession={(id) => closeMutation.mutate(id)}
              replyInput={replyInput}
              onReplyInputChange={setReplyInput}
              onSendReply={handleSendReply}
              onReplyKeyDown={handleReplyKeyDown}
              isReplying={replyMutation.isPending}
              markAsBenchmark={markAsBenchmark}
              onMarkAsBenchmarkChange={setMarkAsBenchmark}
            />
          </TabsContent>

          <TabsContent value="preferences">
            <ScenePreferenceCard />
          </TabsContent>

          <TabsContent value="optimize">
            <OptimizeTab
              questions={unmatchedQuestions}
              onUpdateStatus={(id, status) => unmatchedStatusMutation.mutate({ id, status })}
              onExport={() => aiKefuService.exportUnmatchedQuestions()}
            />
          </TabsContent>
        </Tabs>
      </div>

      <TakeoverDialog
        open={takeoverTarget !== null}
        sessionId={takeoverTarget}
        onClose={() => setTakeoverTarget(null)}
        onConfirm={(servicerUserid) =>
          takeoverTarget && takeoverMutation.mutate({ sessionId: takeoverTarget, servicerUserid })
        }
        submitting={takeoverMutation.isPending}
      />
    </>
  );
}
